// ============================================================================
// LA MATRICE DE L'ASSISTANT GAMME — qui existe, et qui doit recevoir un code.
//
// ⚠️ POURQUOI CES FONCTIONS VIVENT HORS DE L'ÉCRAN. La règle « quelles cases faut-il allouer ? »
// est la seule chose de l'étape 3 qui puisse se tromper en silence, et c'est aussi la seule qu'un
// rendu ne montre pas : elle décide d'un APPEL RÉSEAU qui BRÛLE des numéros de séquence. Sortie ici,
// elle se prouve ; laissée dans l'effet React, elle ne se prouverait que par un test de composant,
// infrastructure que le back-office n'a pas.
// ============================================================================

import type { SecondDimension } from '../api';
import { industryLabel } from './articles-variants-axes';
import { t } from '../i18n';

/** Une COLONNE de la matrice : une taille, et — sur une grille à deux axes — une valeur de dimension. */
export interface SizeAxis { size: string; size2: string | null; label: string }

/**
 * LES COLONNES DE LA MATRICE : chaque taille retenue, croisée avec chaque valeur de la dimension de la
 * grille — comme `wzSizes()` du dessin, qui combine les libellés (« 40/32 »). Sans dimension, ou sans
 * valeur déclarée, la taille seule.
 * ⚠️ LA DIMENSION VIENT DE LA GRILLE, jamais d'un choix par article (divergence dite au dessin, qui
 * offre un sélecteur « Longueurs — facultatif ») : notre modèle n'a qu'une colonne, `size_grid.grid2_id`,
 * qui rattache une grille SECONDAIRE à la grille de tailles.
 * ⚠️ LE LIBELLÉ EST CELUI DE L'AFFICHAGE (`industryLabel`, une seule règle) ; les deux axes restent
 * SÉPARÉS derrière lui — c'est ainsi que la création les écrit.
 */
export function sizeAxes(sizes: readonly string[], dimension: SecondDimension | null): SizeAxis[] {
  const values: (string | null)[] = dimension && dimension.values.length > 0 ? dimension.values : [null];
  return sizes.flatMap((size) => values.map((size2) => ({ size, size2, label: industryLabel(size, size2) })));
}

/** La clé d'une case — un seul endroit la compose, sinon deux formats finissent par coexister. */
export const cleCase = (couleur: string, taille: string): string => `${couleur}|${taille}`;

/** Une case : sa clé, sa couleur, le libellé de sa colonne (`s`), et ses deux axes SÉPARÉS pour l'écriture. */
export interface CaseMatrice { k: string; c: string; s: string; size: string; size2: string | null }

/**
 * Toutes les déclinaisons POSSIBLES : chaque couleur croisée à chaque colonne retenue.
 * ⚠️ Une colonne donnée en CHAÎNE est une taille sans second axe — la forme des grilles à un axe, et
 * celle des tests écrits avant le second axe.
 */
export function casesDeLaMatrice(
  couleurs: readonly string[],
  axes: readonly (string | SizeAxis)[],
): CaseMatrice[] {
  return couleurs.flatMap((c) => axes.map((a) => {
    const axis: SizeAxis = typeof a === 'string' ? { size: a, size2: null, label: a } : a;
    return { k: cleCase(c, axis.label), c, s: axis.label, size: axis.size, size2: axis.size2 };
  }));
}

/**
 * LES CASES QU'IL FAUT ALLOUER MAINTENANT — le garde-fou de la reprise automatique.
 *
 * ⚠️ TROIS RAISONS DE NE RIEN DEMANDER, et il faut les trois :
 *   · le mode fournisseur — les codes viennent de la douchette, en allouer serait les écraser ;
 *   · une case EXCLUE — l'utilisateur l'a écartée, lui rendre un code annulerait son geste (et c'est
 *     pour ça que les exclusions sont STOCKÉES : déduites de « case sans code », elles ne survivraient
 *     pas à un aller-retour vers l'étape précédente) ;
 *   · un ÉCHEC déjà rencontré — c'est le garde-fou anti-boucle. Une allocation refusée ne remplit
 *     aucune case : sans cette porte, le rendu suivant reverrait les mêmes cases manquantes et
 *     rappellerait le serveur, indéfiniment. On s'arrête au premier refus ; un geste explicite réarme.
 */
export function casesAAllouer(etat: {
  cases: readonly CaseMatrice[];
  codes: Readonly<Record<string, string>>;
  exclues: Readonly<Record<string, true>>;
  /** Le motif du dernier refus, ou `null` si l'allocation n'a pas encore échoué. */
  failure: string | null;
  auto: boolean;
  /** Plafond de déclinaisons par création, servi par le serveur. */
  max: number;
}): string[] {
  if (!etat.auto || etat.failure !== null) return [];
  const toCreate = etat.cases.filter((x) => !etat.exclues[x.k]);
  // ⚠️ AU-DELÀ DU PLAFOND, ON N'ALLOUE RIEN — pas même les premières (correctif d'audit 2026-08-25).
  //    Un code-barres alloué est DÉFINITIVEMENT consommé (la séquence n'est jamais rendue) : remplir
  //    la matrice avant de découvrir, à la création, qu'elle est trop grande brûlait jusqu'à 120 codes
  //    pour rien. En allouer une partie serait pire : la matrice paraîtrait à moitié prête, et
  //    l'opérateur chercherait pourquoi certaines cases restent vides.
  // ⚠️ ET L'ÉCRAN LE DIT : `matriceHorsBornes` s'affiche à l'étape 3 en même temps que ce refus. Sans
  //    ça, ré-inclure une case au-delà du plafond ne faisait RIEN, en silence (revue 2026-08-25).
  if (toCreate.length > etat.max) return [];
  return toCreate.filter((x) => (etat.codes[x.k] ?? '') === '').map((x) => x.k);
}

/**
 * COMBIEN DE DÉCLINAISONS LA CRÉATION VA RECEVOIR — le seul compte que le plafond ait le droit de juger.
 *
 * ⚠️ IL DÉPEND DU MODE, et c'est tout l'objet de cette fonction (revue 2026-08-25). L'envoi ne contient
 * que les cases PORTANT UN CODE (`creerLaGamme`) : en mode automatique elles seront toutes codées, donc
 * le compte est celui des cases non exclues ; en mode FOURNISSEUR seules les cases scannées partent, et
 * borner sur la taille de la matrice refusait une gamme de 12×10 dont l'opérateur ne scannait que 40
 * déclinaisons — un envoi que le serveur aurait accepté sans broncher.
 */
export function variantsToCreate(
  cases: readonly CaseMatrice[],
  codes: Readonly<Record<string, string>>,
  exclues: Readonly<Record<string, true>>,
  auto: boolean,
): number {
  return auto
    ? cases.filter((x) => !exclues[x.k]).length
    : cases.filter((x) => (codes[x.k] ?? '').trim() !== '').length;
}

/** Où l'opérateur se trouve quand le refus s'affiche — c'est ce qui décide du geste qu'on lui propose. */
export type EtapeDeLaMatrice = 'composition' | 'codes';

/**
 * Le motif qui empêche de poursuivre — `null` quand la matrice est créable.
 *
 * ⚠️ IL SE DIT TÔT, à l'étape où la matrice se compose : découvrir la borne au moment de créer, c'est
 * découvrir qu'on a saisi des coloris, des tailles et des prix pour rien.
 * ⚠️ LE REMÈDE PROPOSÉ DOIT ÊTRE FAISABLE DEPUIS L'ÉCRAN QUI L'AFFICHE (revue 2026-08-25). La première
 * version conseillait « excluez des cases » à l'étape 2 — or exclure est un geste de l'étape 3, et le
 * refus interdisait précisément d'y aller. Prescrire une issue fermée est pire que ne rien prescrire :
 * l'opérateur cherche le bouton, puis se croit fautif.
 */
export function matriceHorsBornes(toCreate: number, max: number, ou: EtapeDeLaMatrice): string | null {
  if (toCreate <= max) return null;
  const remede = ou === 'composition'
    ? t('Retirez des coloris ou des tailles, puis reprenez.')
    : t('Excluez des cases, ou revenez à l’étape précédente retirer un coloris ou une taille.');
  return t('Cette gamme créerait {declinaisons}, le maximum est de {max} par création. {remede}', { declinaisons: t('{n} déclinaison', { n: toCreate }), max, remede });
}
